import React, { useEffect, useRef } from "react"
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Animated,
  Easing
} from "react-native"
import moment from "moment"
import { COLORS } from "../../screens/tenants/tenantOnboarding/screens/components/PropertyDetailsCard"
import getPlatformType from "../../helpers/helpers"

interface Props {
  data: any[]
}

const layoutType = getPlatformType()

export default function Timeline({ data = [] }: Props) {

  const isMobile = layoutType == "phone"

  const progressAnim = useRef(new Animated.Value(0)).current

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 1,
      duration: 800,
      easing: Easing.out(Easing.ease),
      useNativeDriver: false,
    }).start()
  }, [])

  const renderItem = ({ item, index }: any) => {

    const user = item.user
    const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`
    const date = moment(item.createdAt).format("DD MMM YYYY")
    const isLast = index === data.length - 1

    const slideAnim = new Animated.Value(30)
    const fadeAnim = new Animated.Value(0)

    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 500,
        delay: index * 120,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        delay: index * 120,
        useNativeDriver: true,
      }),
    ]).start()

    return (
      <Animated.View
        style={[
          styles.rowWrapper,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >

        {/* LEFT SIDE */}
        <View style={styles.leftSide}>
          <View style={styles.dot} />

          {!isLast && (
            <Animated.View
              style={[
                styles.line,
                {
                  height: progressAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 50],
                  }),
                },
              ]}
            />
          )}
        </View>

        {/* CARD */}
        <View style={[styles.card, isMobile && styles.mobileCard]}>

          <View style={[styles.topRow, isMobile && styles.mobileTopRow]}>
            <Text style={styles.comment} numberOfLines={1}>
              {item.comment}
            </Text>
            <Text style={styles.date}>{date}</Text>
          </View>

          <View style={styles.userRow}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {user?.first_name?.charAt(0)?.toUpperCase() || "U"}
              </Text>
            </View>

            <View>
              <Text style={styles.userName}>{fullName}</Text>
              <Text style={styles.userEmail} numberOfLines={1}>
                {user?.email}
              </Text>
            </View>
          </View>

        </View>
      </Animated.View>
    )
  }

  return (
    <View style={[styles.container, isMobile && styles.mobileContainer]}>
      <Text style={styles.heading}>Timeline</Text>

      <FlatList
        data={data}
        keyExtractor={(item) =>
          item.open_flow_maintenance_project_comment_id.toString()
        }
        renderItem={renderItem}
        contentContainerStyle={{ paddingTop: 5 }}
      />
    </View>
  )
}


const styles = StyleSheet.create({

    container: {
      backgroundColor: "#fff",
      borderRadius: 16,
      padding: 20,
      marginTop: 16,
      elevation: 2,
    },
  
    mobileContainer: {
      padding: 14,
    },
  
    heading: {
      fontSize: 18,
      fontWeight: "700",
      color: COLORS.primary,
      marginBottom: 14,
    },
  
    rowWrapper: {
      flexDirection: "row",
      marginBottom: 18,
    },
  
    leftSide: {
      width: 28,
      alignItems: "center",
    },
  
    dot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: COLORS.primary,
      marginTop: 6,
    },
  
    line: {
      width: 2,
      backgroundColor: "#E5E7EB",
      marginTop: 4,
    },
  
    card: {
      flex: 1,
      backgroundColor: "#F3F4F6",
      borderRadius: 12,
      padding: 14,
      marginLeft: 10,
    },
  
    mobileCard: {
      padding: 12,
    },
  
    topRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
  
    mobileTopRow: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  
    comment: {
      fontSize: 14,
      fontWeight: "600",
      color: "#374151",
      flex: 1,
    },
  
    date: {
      fontSize: 12,
      color: "#9CA3AF",
      marginLeft: 8,
    },
  
    userRow: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: 10,
    },
  
    avatar: {
      width: 30,
      height: 30,
      borderRadius: 15,
      backgroundColor: COLORS.primary + "20",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 10,
    },
  
    avatarText: {
      color: COLORS.primary,
      fontWeight: "700",
      fontSize: 13,
    },
  
    userName: {
      fontSize: 13,
      fontWeight: "600",
      color: "#374151",
    },
  
    userEmail: {
      fontSize: 11,
      color: "#9CA3AF",
    },
  })
  